import { isObject } from 'lodash';
import { ElementCategoryID } from '../models/element-categories.model';
import {
  ElementPropertySource,
  IElementProperty,
} from '../models/element-property.model';
import type { Recipe } from '../models/recipe.model';
import type { OneOfListElements } from '../models/project.model';
import type { OneOfFactoryElements } from '../models/factory-element.model';

export type UnknownCategoryId = ElementCategoryID | string | undefined;

type OneOfTypesWithCategoryId = OneOfListElements | IElementProperty | Recipe;

const elementCategoryIdSet = new Set<string>(
  Object.values(ElementCategoryID as Record<string, string>),
);

/**
 * Detect whether a string is a known {@link ElementCategoryID}.
 */
export const isElementCategoryID = (str: unknown): str is ElementCategoryID => {
  return typeof str === 'string' && elementCategoryIdSet.has(str);
};

/**
 * Resolve the category id from an element-like value, category id string, or nested category reference.
 * Does not import the categories registry so category models (e.g. slab/wall recipe filters) can load safely.
 */
export const getElementCategoryId = (
  elementOrId:
    | UnknownCategoryId
    | OneOfTypesWithCategoryId
    | OneOfFactoryElements
    | undefined,
): ElementCategoryID => {
  if (typeof elementOrId === 'string') {
    return isElementCategoryID(elementOrId)
      ? elementOrId
      : ElementCategoryID.None;
  }
  if (isObject(elementOrId) && ElementPropertySource.Category in elementOrId) {
    return getElementCategoryId(elementOrId.category_id);
  }
  return ElementCategoryID.None;
};
